import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useResume } from '../../context/ResumeContext';
import {
    monitorForElements,
    draggable,
    dropTargetForElements
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
    attachClosestEdge,
    extractClosestEdge
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

const SECTION_LABELS = {
    summary: 'Summary',
    experience: 'Experience',
    education: 'Education',
    projects: 'Projects',
    certifications: 'Certifications',
    awards: 'Awards',
    skills: 'Technical Skills',
};

// Sections that contain reorderable items
const ITEM_SECTIONS = ['experience', 'education', 'projects', 'certifications', 'awards'];

const SECTION_TO_TYPE = {
    experience: 'experience-item',
    education: 'education-item',
    projects: 'projects-item',
    certifications: 'cert-item',
    awards: 'award-item',
};

const TYPE_TO_SECTION_KEY = {
    'experience-item': 'experience',
    'projects-item': 'projects',
    'education-item': 'education',
    'cert-item': 'certifications',
    'award-item': 'awards',
};

const hasText = (v) => Boolean((v || '').toString().trim());
const bulletsHaveText = (b) =>
    String(b || '').split('\n').map(l => l.replace(/^\s*[•\-*]\s*/, '').trim()).filter(Boolean).length > 0;

// Mirror the generator's "is this entry real?" logic so the panel only lists
// entries that will actually appear in the PDF (no blank placeholders).
const itemHasContent = (sectionId, x) => {
    switch (sectionId) {
        case 'experience': return hasText(x.role) || hasText(x.company) || bulletsHaveText(x.bullets);
        case 'education': return hasText(x.school) || hasText(x.degree);
        case 'projects': return hasText(x.name) || bulletsHaveText(x.relevance);
        case 'certifications':
        case 'awards': return hasText(x.name);
        default: return false;
    }
};

const GripDots = () => (
    <span className="grid grid-cols-2 gap-[2px] text-current">
        {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="block h-[3px] w-[3px] rounded-full bg-current" />
        ))}
    </span>
);

// --- Draggable Row ---
const DraggableRow = ({ id, type, index, dataId, sectionId, isHeader, children }) => {
    const ref = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [closestEdge, setClosestEdge] = useState(null);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;
        return combine(
            draggable({
                element,
                getInitialData: () => ({ id, index, type, dataId, sectionId }),
                onDragStart: () => setIsDragging(true),
                onDrop: () => setIsDragging(false),
            }),
            dropTargetForElements({
                element,
                getData: ({ input }) =>
                    attachClosestEdge({ id, index, type, dataId, sectionId }, { element, input, allowedEdges: ['top', 'bottom'] }),
                onDragEnter: ({ self }) => setClosestEdge(extractClosestEdge(self.data)),
                onDragLeave: () => setClosestEdge(null),
                onDrop: () => { setClosestEdge(null); setIsDragging(false); },
            })
        );
    }, [id, index, type, dataId, sectionId]);

    const edgeClass =
        closestEdge === 'top' ? 'before:absolute before:left-2 before:right-2 before:-top-px before:h-0.5 before:bg-cyan-400 before:rounded-full'
            : closestEdge === 'bottom' ? 'after:absolute after:left-2 after:right-2 after:-bottom-px after:h-0.5 after:bg-cyan-400 after:rounded-full'
                : '';

    return (
        <div
            ref={ref}
            className={`group relative flex items-center gap-2 rounded-md cursor-grab transition-colors
                ${isHeader ? 'px-2.5 py-2 mt-1' : 'ml-3 px-2.5 py-1.5'}
                ${isDragging ? 'opacity-40' : 'hover:bg-white/5'} ${edgeClass}`}
        >
            <span className={`shrink-0 opacity-0 group-hover:opacity-60 transition-opacity ${isHeader ? 'text-cyan-300' : 'text-neutral-500'}`}>
                <GripDots />
            </span>
            {children}
        </div>
    );
};

// --- Main Panel ---
const SectionReorderPanel = ({ sectionOrder, onReorderSections }) => {
    const { experiences, projects, educations, certifications, awards, reorderSection } = useResume();

    const sectionItems = useMemo(() => ({
        experience: experiences,
        education: educations,
        projects: projects,
        certifications: certifications,
        awards: awards,
    }), [experiences, educations, projects, certifications, awards]);

    const getItemLabel = (sectionId, item) => {
        switch (sectionId) {
            case 'experience': return [item.role, item.company].filter(Boolean).join(' · ');
            case 'education': return item.school || item.degree;
            default: return item.name;
        }
    };

    useEffect(() => {
        return monitorForElements({
            onDrop({ source, location }) {
                const destination = location.current.dropTargets[0];
                if (!destination) return;
                const srcType = source.data.type;
                const dstType = destination.data.type;

                if (srcType === 'header' && dstType === 'header') {
                    const srcSectionId = source.data.sectionId;
                    const dstSectionId = destination.data.sectionId;
                    if (srcSectionId === dstSectionId) return;
                    const newOrder = [...sectionOrder];
                    const srcIdx = newOrder.indexOf(srcSectionId);
                    const dstIdx = newOrder.indexOf(dstSectionId);
                    if (srcIdx !== -1 && dstIdx !== -1) {
                        newOrder.splice(srcIdx, 1);
                        newOrder.splice(dstIdx, 0, srcSectionId);
                        onReorderSections(newOrder);
                    }
                    return;
                }

                if (srcType !== dstType) return;
                const sectionKey = TYPE_TO_SECTION_KEY[srcType];
                if (!sectionKey) return;
                const sourceArray = sectionItems[sectionKey];
                const srcId = source.data.dataId;
                const dstId = destination.data.dataId;
                if (sourceArray && reorderSection) {
                    const srcIdx = sourceArray.findIndex(i => i.id === srcId);
                    const dstIdx = sourceArray.findIndex(i => i.id === dstId);
                    if (srcIdx !== -1 && dstIdx !== -1 && srcIdx !== dstIdx) {
                        reorderSection(sectionKey, srcIdx, dstIdx);
                    }
                }
            },
        });
    }, [sectionOrder, onReorderSections, sectionItems, reorderSection]);

    return (
        <div className="rounded-xl border border-white/10 bg-[#0b0e14]/60 backdrop-blur-sm p-4">
            <div className="flex items-center gap-2 mb-3">
                <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400">
                    Reorder Sections
                </h2>
            </div>
            <p className="text-[11px] text-neutral-500 mb-3 leading-snug">
                Drag sections or entries to reorder — the preview updates live.
            </p>

            <div>
                {sectionOrder.map((sectionId, sectionIndex) => {
                    const items = (ITEM_SECTIONS.includes(sectionId) ? (sectionItems[sectionId] || []) : [])
                        .filter(x => itemHasContent(sectionId, x));
                    return (
                        <div key={sectionId} className="border-b border-white/5 last:border-0 pb-1">
                            <DraggableRow id={sectionId} type="header" index={sectionIndex} sectionId={sectionId} isHeader>
                                <span className="text-[12px] font-semibold uppercase tracking-wide text-neutral-200">
                                    {SECTION_LABELS[sectionId] || sectionId}
                                </span>
                            </DraggableRow>
                            {items.map((item, itemIndex) => (
                                <DraggableRow
                                    key={item.id}
                                    id={item.id}
                                    type={SECTION_TO_TYPE[sectionId]}
                                    index={itemIndex}
                                    dataId={item.id}
                                    sectionId={sectionId}
                                    isHeader={false}
                                >
                                    <span className="text-[12px] text-neutral-400 truncate group-hover:text-neutral-200 transition-colors">
                                        {getItemLabel(sectionId, item)}
                                    </span>
                                </DraggableRow>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SectionReorderPanel;
